import React from 'react'
import { Menu, MenuItem, MenuProps, Switch } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import YumSwitch from '../YumSwitch/YumSwitch'

// Define the interface for menu items
export interface YumMenuItem {
  id: string
  label: string
  onClick: () => void
  disabled?: boolean
  icon?: React.ReactNode
  toggle?: boolean // Whether to show a toggle switch
  toggleValue?: boolean // The current value of the toggle
  onToggleChange?: (checked: boolean) => void // Callback for toggle changes
}

// Extend MenuProps to include our custom props
export interface YumMenuProps extends Omit<MenuProps, 'children'> {
  items: YumMenuItem[]
  menuId?: string
  ariaLabelledBy?: string
}

// Custom styles for menu items
const useStyles = makeStyles({
  menuItem: {
    padding: '8px 16px',
    fontSize: '0.875rem',
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    justifyContent: 'space-between', // Space between content and toggle
    '&:first-child': {
      borderTop: 'none !important',
    },
  },
  menuItemContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flex: 1,
  },
  menuPaper: {
    boxShadow: '0px 5px 15px rgba(0, 0, 0, 0.1)',
    borderRadius: '8px',
  },
  toggle: {
    marginLeft: '8px',
    '& .MuiSwitch-thumb': {
      width: '16px',
      height: '16px',
    },
    '& .MuiSwitch-track': {
      width: '28px',
      height: '16px',
      borderRadius: '8px',
    },
  },
})

/**
 * YumMenu - A reusable menu component with consistent styling
 *
 * This component provides a standardized menu interface that can be used
 * throughout the application. It includes proper accessibility attributes
 * and consistent styling for menu items.
 *
 * @param items - Array of menu items to display
 * @param menuId - Optional ID for the menu element
 * @param ariaLabelledBy - Optional aria-labelledby attribute for accessibility
 * @param ...otherProps - All other Material-UI Menu props
 */
const YumMenu: React.FC<YumMenuProps> = ({ items, menuId = 'yum-menu', ariaLabelledBy, ...otherProps }) => {
  const classes = useStyles()

  const handleToggleClick = (e: React.MouseEvent, item: YumMenuItem) => {
    e.stopPropagation() // Prevent menu item click
    if (item.onToggleChange) {
      item.onToggleChange(!item.toggleValue)
    }
  }

  return (
    <Menu
      id={menuId}
      getContentAnchorEl={null}
      MenuListProps={{
        'aria-labelledby': ariaLabelledBy,
        role: 'menu',
        dense: true,
      }}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      PaperProps={{
        elevation: 2,
        className: classes.menuPaper,
      }}
      style={{ marginTop: 5 }}
      {...otherProps}
    >
      {/* MenuItems must be DIRECT children of Menu/MenuList — MUI manages keyboard
          focus by ref-cloning its direct children, so wrapping them in a <div>
          breaks arrow-key navigation and focus (WCAG 2.1.1). */}
      {items.map((item) => (
        <MenuItem
          key={item.id}
          onClick={item.onClick}
          role="menuitem"
          aria-label={item.label}
          className={classes.menuItem}
          disabled={item.disabled}
          style={{
            paddingRight: item.toggle ? '10px' : '16px',
          }}
        >
          <div className={classes.menuItemContent}>
            {item.icon && item.icon}
            {item.label}
          </div>
          {item.toggle && <YumSwitch checked={item.toggleValue || false} size={'small'} disabled={item.disabled} />}
        </MenuItem>
      ))}
    </Menu>
  )
}

export default YumMenu
